import axios from 'axios';
import { getLanguage } from '../i18n';

// Language config map
const languageConfig = {
  pt: { hl: 'pt-BR', gl: 'br' },
  en: { hl: 'en-US', gl: 'us' },
  es: { hl: 'es-ES', gl: 'es' }
};

// Translation strings for mock results
const mockTranslations = {
  pt: {
    title1: 'Resultados para "{query}" - Exemplo 1',
    desc1: 'Esta e uma descricao de exemplo para a busca "{query}". Configure sua API Key do Google para obter resultados reais.',
    title2: '{query} - Guia Completo',
    desc2: 'Guia completo sobre {query}. Encontre informacoes detalhadas e tutoriais passo a passo.',
    title3: 'Tudo sobre {query}',
    desc3: 'Descubra tudo o que voce precisa saber sobre {query}. Artigos, videos e recursos gratuitos.'
  },
  en: {
    title1: 'Results for "{query}" - Example 1',
    desc1: 'This is an example description for the search "{query}". Configure your Google API Key to get real results.',
    title2: '{query} - Complete Guide',
    desc2: 'Complete guide about {query}. Find detailed information and step-by-step tutorials.',
    title3: 'All about {query}',
    desc3: 'Discover everything you need to know about {query}. Articles, videos and free resources.'
  },
  es: {
    title1: 'Resultados para "{query}" - Ejemplo 1',
    desc1: 'Esta es una descripcion de ejemplo para la busqueda "{query}". Configure su API Key de Google para obtener resultados reales.',
    title2: '{query} - Guia Completa',
    desc2: 'Guia completa sobre {query}. Encuentra informacion detallada y tutoriales paso a paso.',
    title3: 'Todo sobre {query}',
    desc3: 'Descubre todo lo que necesitas saber sobre {query}. Articulos, videos y recursos gratuitos.'
  }
};

// Search suggestions by language
const suggestionSuffixes = {
  pt: [' o que e', ' como funciona', ' preco', ' download', ' gratis', ' online', ' tutorial', ' significado'],
  en: [' what is', ' how it works', ' price', ' download', ' free', ' online', ' tutorial', ' meaning'],
  es: [' que es', ' como funciona', ' precio', ' descargar', ' gratis', ' online', ' tutorial', ' significado']
};

// Search function
export const searchWeb = async (query, options = {}) => {
  try {
    if (process.env.REACT_APP_GOOGLE_API_KEY && process.env.REACT_APP_GOOGLE_CSE_CX) {
      return searchGoogle(query);
    }
    return generateMockResults(query);
  } catch (error) {
    console.error('Search error:', error);
    return generateMockResults(query);
  }
};

// Google Custom Search API
const searchGoogle = async (query) => {
  const language = getLanguage();
  const config = languageConfig[language] || languageConfig.pt;

  try {
    const response = await axios.get('https://www.googleapis.com/customsearch/v1', {
      params: {
        key: process.env.REACT_APP_GOOGLE_API_KEY,
        cx: process.env.REACT_APP_GOOGLE_CSE_CX,
        q: query,
        num: 10,
        safe: 'active',
        hl: config.hl,
        gl: config.gl
      }
    });

    const results = response.data.items?.map((item, index) => ({
      id: index,
      title: item.title,
      url: item.link,
      description: item.snippet,
      favicon: getFaviconUrl(item.link)
    })) || [];

    return {
      results,
      totalResults: parseInt(response.data.searchInformation?.totalResults || 0),
      searchTime: parseFloat(response.data.searchInformation?.searchTime || 0)
    };
  } catch (error) {
    console.error('Google search error:', error);

    if (error.response) {
      console.error('Status:', error.response.status);
      console.error('Data:', error.response.data);

      if (error.response.status === 403) {
        console.error('Error 403: Check if API Key is correct and API is enabled in Google Cloud Console');
      } else if (error.response.status === 400) {
        console.error('Error 400: Check if Custom Search Engine ID (cx) is correct');
      }
    }

    return generateMockResults(query);
  }
};

// Generate mock results
const generateMockResults = (query) => {
  const language = getLanguage();
  const t = mockTranslations[language] || mockTranslations.pt;

  const mockResults = [
    {
      id: 1,
      title: t.title1.replace(/{query}/g, query),
      url: `https://example1.com/${query.replace(/\s+/g, '-')}`,
      description: t.desc1.replace(/{query}/g, query),
      favicon: '🌐'
    },
    {
      id: 2,
      title: t.title2.replace(/{query}/g, query),
      url: `https://example2.com/guide/${query.replace(/\s+/g, '-')}`,
      description: t.desc2.replace(/{query}/g, query),
      favicon: '📚'
    },
    {
      id: 3,
      title: t.title3.replace(/{query}/g, query),
      url: `https://example3.com/${query.replace(/\s+/g, '-')}`,
      description: t.desc3.replace(/{query}/g, query),
      favicon: '🔍'
    }
  ];

  return {
    results: mockResults,
    totalResults: mockResults.length,
    searchTime: 0.1,
    isMock: true
  };
};

// Get favicon URL
const getFaviconUrl = (url) => {
  try {
    const domain = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
  } catch {
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iOCIgY3k9IjgiIHI9IjciIHN0cm9rZT0iIzY2NjY2NiIgc3Ryb2tlLXdpZHRoPSIxLjUiLz4KPHBhdGggZD0iTTggNEw4IDEyTTQgOEwxMiA4IiBzdHJva2U9IiM2NjY2NjYiIHN0cm9rZS13aWR0aD0iMS41IiBzdHJva2UtbGluZWNhcD0icm91bmQiLz4KPC9zdmc+';
  }
};

// Get search suggestions
export const getSearchSuggestions = async (query) => {
  const language = getLanguage();
  const config = languageConfig[language] || languageConfig.pt;

  if (process.env.REACT_APP_GOOGLE_API_KEY) {
    try {
      const response = await axios.get('https://suggestqueries.google.com/complete/search', {
        params: {
          client: 'firefox',
          q: query,
          hl: config.hl
        }
      });

      if (Array.isArray(response.data) && response.data[1]) {
        return response.data[1].slice(0, 8);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  }

  return generateLocalSuggestions(query);
};

// Generate local suggestions
const generateLocalSuggestions = (query) => {
  const language = getLanguage();
  const suffixes = suggestionSuffixes[language] || suggestionSuffixes.pt;

  return suffixes.map(suffix => query + suffix).slice(0, 8);
};
